import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import { DEFAULT_ASPECT_TYPE, pendingSelectionAtom } from "../selection/selectionAtoms.ts";
import type { AspectType } from "../selection/types.ts";
import { useSelectedAspect } from "../selection/useSelectedAspect.ts";
import { useSetSelectedNodeId } from "../selection/useSelectedNode.ts";
import type { NodeLink } from "./useGetNodeLink.ts";

/**
 * Follow a link from `useGetNodeLink`: switch to the page it names, and select on arrival where it
 * names something to select.
 *
 * Selection can't just follow the navigate — the route change resets it — so a selection on another
 * route rides `pendingSelectionAtom` and is applied once the pathname settles.
 */
export const useFollowNodeLink = () => {
  const setSelectedNodeId = useSetSelectedNodeId();
  const [, setSelectedAspect] = useSelectedAspect();
  const setPendingSelection = useSetAtom(pendingSelectionAtom);
  const navigate = useNavigate();

  return useCallback(
    (link: NodeLink, aspect: AspectType = DEFAULT_ASPECT_TYPE) => {
      // Read live rather than from the render this was made in: long-lived UI holds on to a jump —
      // the blocked-cycles toast stays up across the jumps it offers — so the route to compare
      // against is whichever one the author is on when the jump is followed.
      const alreadyThere = !link.path || link.path === window.location.pathname;

      if (link.kind === "page") {
        if (!alreadyThere) void navigate(link.path)?.catch(console.warn);
        return;
      }

      if (alreadyThere) {
        setSelectedNodeId(link.selectNodeId);
        setSelectedAspect(aspect);
        return;
      }
      setPendingSelection({ nodeId: link.selectNodeId, aspect, path: link.path });
      void navigate(link.path)?.catch(console.warn);
    },
    [navigate, setPendingSelection, setSelectedAspect, setSelectedNodeId],
  );
};
